import clsx from 'clsx'
import RightArrowIcon from '@/CustomIcons/RightArrowIcon'
import RadioCircleIcon from '@/CustomIcons/RadioCircleIcon'
import { IconCircle } from '@/components/CircleIcon'
import { Button } from '@/UIComponents2/Button'
import DotsMenuIcon from '@/CustomIcons/DotsMenuIcon'
import { ScheduledSessionOfUser } from '@/AppContext'
import { DateTime } from 'luxon'
import { useHistory } from 'react-router-dom'
import { SimpleMenu } from '@/UIComponents2/SimpleMenu'
import { useState } from 'react'
import { copyToClipboard } from '@/utils'
import { BalloonText } from '@/components/BalloonText'
import { useTranslation } from 'react-i18next'

type ScheduledSessionCardProps = {
    className?: string
    session: ScheduledSessionOfUser
    onEdit: () => void
    onDelete: () => void
}
export function ScheduledSessionCard({ className, session, onEdit, onDelete }: ScheduledSessionCardProps) {
    const { t } = useTranslation()
    const history = useHistory()
    const start = DateTime.fromISO(session.scheduledStartTime)
    const end = DateTime.fromISO(session.scheduledEndTime)
    const active = isActive(start, end)

    const [copied, setCopied] = useState(false)
    const handleCopy = () => {
        copyToClipboard(session.joinLink)
        setCopied(true)
        setTimeout(() => {
            setCopied(false)
        }, 700)
    }

    const border = 'border-muse-toplit-low rounded-[26px]'
    const bg = 'bg-[rgba(255,255,255,0.03)]'
    const layout = 'flex flex-col p-3 gap-2'
    return (
        <div className={clsx('group ', border, bg, layout, className)}>
            <div className="flex justify-between items-start">
                <div className="flex gap-4 items-start">
                    <div>
                        <IconCircle className="relative w-[40px] h-[40px]" pulse={active}>
                            <RadioCircleIcon
                                className={clsx(active && 'text-[#85E0A3]')}
                                style={{ fontSize: '20px' }}
                            />
                        </IconCircle>
                    </div>
                    <div>
                        <div className="flex flex-col justify-around">
                            <span className="t-base">{session.name}</span>
                            <span className="t-sm-light opacity-50">{session.host.username}</span>
                        </div>
                    </div>
                </div>
                <div className="t-sm-light opacity-50 pr-1">{start.toFormat('LLL dd')}</div>
            </div>
            <div className="flex justify-between items-end pl-1">
                <div>
                    <span className="t-big-light">
                        {start.toFormat('h:mm')} - {end.toFormat('h:mm')}
                    </span>
                    <span className="t-sm-light pl-1 opacity-50">{end.toFormat('a').toLowerCase()}</span>
                </div>
                <div className="flex items-center gap-2">
                    <SimpleMenu
                        items={[
                            { label: t('Edit Session'), onClick: onEdit },
                            { label: t('Delete Session'), onClick: onDelete },
                            {
                                label: (
                                    <div className="relative overflow-visible">
                                        {t('Copy Session Link')}
                                        <BalloonText active={copied} text={t('Copied!')} />
                                    </div>
                                ),
                                onClick: handleCopy,
                                closeOnSelect: false,
                            },
                        ]}
                    >
                        <Button className="py-1 px-1 opacity-70">
                            <DotsMenuIcon style={{ fontSize: '1.3rem' }} />
                        </Button>
                    </SimpleMenu>
                    <Button
                        variant={active ? 'glow' : 'secondary'}
                        className="py-0 px-0 w-[48px] h-[40px]"
                        onClick={() => {
                            history.push(`/join/${session.joinCode}`)
                        }}
                    >
                        <RightArrowIcon />
                    </Button>
                </div>
            </div>
        </div>
    )
}

function isActive(start: DateTime, end: DateTime) {
    const startDiffMinutes = start.diffNow().as('minutes')
    const endDiffMinutes = end.diffNow().as('minutes')

    const started = startDiffMinutes < 5 // starts within 5 minutes
    const ended = endDiffMinutes < -5 // ended within the last 5 minutes

    return started && !ended
}
